import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const RouteCoverage = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Route Coverage Map" 
        description="Service areas and route assignments"
      >
        <section className="space-y-6">
          <p>
            BlueLux Pool Service covers multiple neighborhoods across San Diego County. This page 
            outlines our service areas and provides helpful route information.
          </p>

          <div className="bg-muted/50 border border-border rounded-lg p-6">
            <p className="text-center text-muted-foreground italic">
              🗺️ Interactive route map coming soon! Contact office for current route assignments.
            </p>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Primary Service Areas</h2>
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4">
              <h3 className="text-lg font-medium mb-2">North County Coastal</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Carlsbad</li>
                <li>Encinitas</li>
                <li>Solana Beach</li>
                <li>Del Mar</li>
              </ul>
            </div>
            <div className="border border-border rounded-lg p-4">
              <h3 className="text-lg font-medium mb-2">North County Inland</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Escondido</li>
                <li>San Marcos</li>
                <li>Vista</li>
                <li>Rancho Bernardo</li>
              </ul>
            </div>
            <div className="border border-border rounded-lg p-4">
              <h3 className="text-lg font-medium mb-2">Central San Diego</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>La Jolla</li>
                <li>UTC/University City</li>
                <li>Mira Mesa</li>
                <li>Scripps Ranch</li>
              </ul>
            </div>
            <div className="border border-border rounded-lg p-4">
              <h3 className="text-lg font-medium mb-2">South Bay</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Chula Vista</li>
                <li>Bonita</li>
                <li>Eastlake</li>
                <li>Otay Ranch</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Route Assignment Process</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">New Technician Assignments</h3>
              <p className="text-muted-foreground">
                Routes are assigned based on your home location to minimize drive time. We aim to keep 
                your service area within 15-20 minutes of your home address.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium">Route Density</h3>
              <p className="text-muted-foreground">
                We optimize routes for efficiency—most stops are within 5-10 minutes of each other. 
                As new customers are added in your area, your route becomes more efficient and profitable.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Average Route Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-primary mb-1">25-35</div>
              <div className="text-muted-foreground">Stops per day</div>
            </div>
            <div className="border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-primary mb-1">6-8 hours</div>
              <div className="text-muted-foreground">Average route time</div>
            </div>
            <div className="border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-primary mb-1">40-60 miles</div>
              <div className="text-muted-foreground">Daily driving distance</div>
            </div>
            <div className="border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-primary mb-1">5 days/week</div>
              <div className="text-muted-foreground">Standard schedule</div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Navigation & Routing Tips</h2>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>Use BlueLux route app for optimal stop sequencing</li>
            <li>Account for traffic patterns (avoid freeway at rush hour)</li>
            <li>Service uphill stops first (easier to carry equipment downhill)</li>
            <li>Group stops by neighborhood to reduce backtracking</li>
            <li>Note customer schedule preferences (some want early service)</li>
          </ul>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">High-Traffic Areas & Parking</h2>
          <p className="text-muted-foreground mb-4">
            Some neighborhoods have parking restrictions or tight streets. Here are tips for common 
            challenging areas:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li><strong>La Jolla Village:</strong> Park on side streets, not in customer driveways (narrow streets)</li>
            <li><strong>Del Mar Heights:</strong> Watch for street sweeping signs on Tuesdays</li>
            <li><strong>Carlsbad Villages:</strong> Metered parking until 6 PM</li>
            <li><strong>Rancho Bernardo:</strong> HOAs have strict guest parking rules—use customer driveway</li>
          </ul>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Gate Codes & Access</h2>
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
            <p className="font-semibold text-destructive mb-2">Security Reminder</p>
            <p className="text-muted-foreground">
              All gate codes and access information stored in encrypted customer database. Never share 
              codes with anyone outside BlueLux. If you notice a gate code has changed, update it in 
              the system immediately.
            </p>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Route Coverage & Substitutes</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">When You're Out</h3>
              <p className="text-muted-foreground">
                If you're sick or on vacation, another technician will cover your route. Leave detailed 
                notes about any ongoing issues or customer preferences to ensure seamless service.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium">Covering for Others</h3>
              <p className="text-muted-foreground">
                You may occasionally be asked to cover stops on another route. Review the customer notes 
                before arriving and maintain the same service standards. You earn the same per-stop rate 
                for coverage stops.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Requesting Route Changes</h2>
          <p className="text-muted-foreground">
            If you need to change your service area due to relocation or other reasons, submit a written 
            request to management. We'll work to accommodate changes when possible, though route 
            availability varies.
          </p>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default RouteCoverage;